import { useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import {PageLink, PageTitle} from '../../../../_metronic/layout/core/PageData'
import {FieldArray, FormikProvider, useFormik} from 'formik'
import { preventEnterSubmit } from '../../../util/Functions'
import { array, object, string } from 'yup'
import { COUNTRIES_EN } from '../../../util/Constants'
import { SubSectionHeader } from '../../../common/component/SubSectionHeader'
import { MessageToast, MessageType } from '../../../common/component/MessageToast'
import { Toast } from 'bootstrap'
import { CustomInputRenderer } from '../../../common/component/CustomInputRenderer'
import {get} from 'lodash'
import { useContactCheckMutation, useContactCreateMutation } from '../Contact.api'
import { ContactDetailsType, IContactCreateRequestDto, IContactDetailDto } from '../../contact/Contact.model'
import FormValidator from '../../../util/FormValidator.util'
import classNames from 'classnames'
import useDebouncedValue from '../../../util/CustomHooks'

interface ICreateFormikSchema {
  displayName: string
  email: string
  telephone: string
  countryCode: string
  country: string
  address: string
  details: IContactDetailDto[]
  photoUrl: string
}

export const ContactNewPage = () => {
  const intl = useIntl()
  const formValidator = new FormValidator(intl)
  const navigate = useNavigate()
  const breadCrumbs: Array<PageLink> = [
    {
      title: intl.formatMessage({id: 'BREADCRUMBS.DIRECTORY'}),
      path: '/contacts',
      isSeparator: false,
      isActive: false,
    },
  ]

  const [loadError, setLoadError] = useState<string | undefined>(undefined)
  const [toastEntry, setToastEntry] = useState<[MessageType, string]>()

  const didCheckData = useRef(false)
  const [isEmailChecked, setIsEmailChecked] = useState<boolean>(false)
  const [isEmailChecking, setIsEmailChecking] = useState<boolean>(false)
  const [isEmailTaken, setIsEmailTaken] = useState<boolean>(false)
  const [email, setEmail] = useState('')
  const debouncedEmailValue = useDebouncedValue(email, 500)
  
  const [validationSchema, setValidationSchema] = useState(
    object().shape({
      displayName: formValidator.validateStringStrict(),
      email: formValidator.validateEmailStrict(),
      telephone: formValidator.validateString(),
      countryCode: formValidator.validateString(),
      address: formValidator.validateString(),
      details: array().of(
        object({
          type: formValidator.validateStringStrict(),
          value: string().when(['type'], {
            is: (value) => value === ContactDetailsType.EMAIL,
            then: () => formValidator.validateEmailStrict(),
            otherwise: () => formValidator.validateStringStrict(),
          }),
        })
      ),
      photoUrl: formValidator.validateString(3000),
    })
  )
  const [initialValues, setInitialValues] = useState<ICreateFormikSchema>({
    displayName: '',
    email: '',
    telephone: '',
    countryCode: '',
    country: '',
    address: '',
    details: [],
    photoUrl: ''
  })

  const [create] = useContactCreateMutation()
  const [check] = useContactCheckMutation()

  const formik = useFormik<ICreateFormikSchema>({
    enableReinitialize: true,
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      try {
        if (values) {
          setSubmitting(true)

          const request: IContactCreateRequestDto = {
            displayName: values.displayName,
            email: values.email,
            telephone: values.telephone,
            countryCode: values.countryCode,
            country: values.countryCode
              ? intl.formatMessage({
                  id: `DROPDOWN.COUNTRY.${values.countryCode}`,
                })
              : '',
            address: values.address,
            details: values.details,
            photoUrl: values.photoUrl,
          }

          const response: any = await create(request).unwrap()
          if (!response || response.error) {
            setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
          } else {
            navigate(`/contacts/${response.id}`)
          }
        }
      } catch (e: any) {
        setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
      } finally {
        setSubmitting(false)
      }
    },
  })

  const {setSubmitting} = formik;

  useEffect(() => {
    if (toastEntry) {
      const [messageType] = toastEntry
      const toastElement = document.getElementById('#CustomToast')
      const delay =
        messageType === MessageType.ERROR || messageType === MessageType.WARNING ? 10000 : 2000
      if (toastElement) {
        toastElement.addEventListener('hidden.bs.toast', () => setToastEntry(undefined))
        const toast = new Toast(toastElement, {autohide: true, delay})
        toast.show()
      }
    }
  }, [toastEntry])

  useEffect(() => {
    const checkEmail = async (email) => {
      try {
        setIsEmailChecking(true)
        if (!didCheckData.current) {
          const response: any = await check({email})
          setIsEmailTaken(response && response.data && !response.data.valid)
          setIsEmailChecked(true)
        }
      } finally {
        setIsEmailChecking(false)
      }
    }

    const value: string = debouncedEmailValue
    formik.values.email = email
    if (value) {
      const validEmail = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)
      if (validEmail) {
        checkEmail(value)
      }
    }
  }, [debouncedEmailValue])

  const removeDetail = (index) => {
    formik.setFieldValue(
      `details`,
      get(formik.values, `details`).filter((_, i) => i !== index)
    )
  }

  const addDetail = () => {
    const details = get(formik.values, 'details') || []
    formik.setFieldValue('details', [...details, {type: ContactDetailsType.OTHER, value: ''}])
  }

  const onEmailChange = async (email) => {
    setIsEmailChecked(false)
    setIsEmailTaken(false)
    setEmail(email)
  }

  const tabWithError = (tabId) => {
    switch (tabId) {
      case 'kt_tab_pane_1': {
        return (
          (formik.touched.displayName && formik.errors.displayName) ||
          (formik.touched.email && formik.errors.email) ||
          (formik.touched.telephone && formik.errors.telephone) ||
          (formik.touched.countryCode && formik.errors.countryCode) ||
          (formik.touched.address && formik.errors.address) ||
          (formik.touched.details && formik.errors.details) ||
          (formik.touched.photoUrl && formik.errors.photoUrl)
        )
      }
      default:
        return false
    }
  }

  return (
    <>
      {toastEntry && <MessageToast messageType={toastEntry[0]} message={toastEntry[1]} />}
      <PageTitle breadcrumbs={breadCrumbs}></PageTitle>
      <div className='card card-flush card-stretch-100 card-bordered mt-5'>
        <div className='card-body'>
          {loadError && (
            <div className='alert alert-danger d-flex align-items-center px-5 py-2 mb-10'>
              <span className='svg-icon svg-icon-2hx svg-icon-primary ms-3 me-5'>
                <i className='bi bi-database-exclamation fs-2'></i>
              </span>

              <div className='d-flex flex-column'>
                <span>{loadError}</span>
              </div>

              <button
                type='button'
                className='position-absolute position-sm-relative m-2 m-sm-0 top-0 end-0 btn btn-icon ms-sm-auto'
                data-bs-dismiss='alert'
              >
                <div className='badge px-3 py-2'>
                  <span className='text-danger fs-1'>&times;</span>
                </div>
              </button>
            </div>
          )}

          <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit} onKeyDown={preventEnterSubmit} noValidate>
              <div className='d-flex flex-row'>
                <div className='d-flex flex-column flex-row-fluid'>
                  <div className='d-flex flex-row flex-column-fluid'>
                    <div className='d-flex flex-row-fluid flex-start'>
                      <div className='d-flex flex-column'>
                        <span className='text-gray-800 fs-2 fw-bolder'>
                          {formik.values?.displayName && formik.values.displayName.length > 0
                            ? formik.values?.displayName.toUpperCase()
                            : intl.formatMessage({id: 'UTIL.CREATE.CONTACT'})}
                        </span>
                      </div>
                    </div>

                    <div className='d-flex flex-row-auto flex-start ms-5'>
                      <button
                        type='submit'
                        className='btn btn-sm btn-primary me-2 d-none d-md-block'
                        onClick={() => formik.handleSubmit()}
                        disabled={formik.isSubmitting || isEmailChecking}
                      >
                        {formik.isSubmitting || isEmailChecking ? (
                          <span className='indicator-progress' style={{display: 'block'}}>
                            {intl.formatMessage({id: 'UTIL.PROCESSING'})}
                            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                          </span>
                        ) : (
                          <>
                            <i className='bi bi-floppy'></i>
                            <span className='indicator-label'>
                              {intl.formatMessage({id: 'UTIL.SAVE'})}
                            </span>
                          </>
                        )}
                      </button>
                      <button
                        type='submit'
                        className='btn btn-sm btn-primary btn-active-color-secondary me-2 d-md-none'
                        onClick={() => formik.handleSubmit()}
                        disabled={formik.isSubmitting || isEmailChecking}
                      >
                        {formik.isSubmitting || isEmailChecking ? (
                          <span className='indicator-progress' style={{display: 'block'}}>
                            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                          </span>
                        ) : (
                          <i className='bi bi-floppy'></i>
                        )}
                      </button>

                      {!formik.isSubmitting && !isEmailChecking && (
                        <button
                          className='btn btn-sm btn-light'
                          onClick={() => navigate(`/contacts`)}
                        >
                          <i className='bi bi-x-lg'></i>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <ul className='nav nav-tabs nav-line-tabs mb-5 fs-6 mt-5'>
                  <li className='nav-item'>
                    <a className='nav-link active' data-bs-toggle='tab' href='#kt_tab_pane_1'>
                      {tabWithError('kt_tab_pane_1') && <span className='text-danger me-1'>*</span>}
                      {intl.formatMessage({id: 'UTIL.INFORMATION'})}
                    </a>
                  </li>
                </ul>
                <div className='tab-content mt-10' id='myTabContent'>
                  <div className='tab-pane fade active show' id='kt_tab_pane_1' role='tabpanel'>
                    <div className='row mb-5'>
                      <SubSectionHeader
                        title={intl.formatMessage({id: 'UTIL.GENERAL'})}
                        separator={false}
                      />

                      <div className='px-5'>
                        <div className='row mb-5'>
                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.DISPLAY_NAME'})}
                            placeholder={intl.formatMessage({id: 'UTIL.DISPLAY_NAME'})}
                            required={true}
                            disabled={formik.isSubmitting}
                            formik={formik}
                            fieldName={`displayName`}
                            enableInputLength={true}
                          ></CustomInputRenderer>

                          <div className='row mb-2'>
                            <label className='d-flex flex-column-auto flex-start align-items-start col-xxl-3 fs-6 col-form-label col-form-label-sm'>
                              <span className='text-gray-700 required'>
                                {intl.formatMessage({id: 'UTIL.EMAIL'})}
                              </span>
                            </label>

                            <div className='col-xxl-9'>
                              <input
                                type='text'
                                className='form-control form-control-sm form-control-solid px-5 py-0 fs-6 text-dark'
                                placeholder={intl.formatMessage({id: 'UTIL.EMAIL'})}
                                {...formik.getFieldProps(`email`)}
                                disabled={formik.isSubmitting || isEmailChecking}
                                onKeyUp={(e) => onEmailChange(e.currentTarget.value)}
                              />
                              <div className='d-flex flex-row p-1'>
                                <div className='d-flex flex-column flex-row-fluid'>
                                  <div className='d-flex flex-row flex-column-fluid'>
                                    <div className='d-flex flex-row-fluid flex-start'>
                                      {get(formik.touched, `email`) &&
                                      get(formik.errors, `email`) ? (
                                        <span className='fs-8 text-danger'>
                                          {get(formik.errors, `email`)?.toUpperCase() ?? ''}
                                        </span>
                                      ) : (
                                        <>
                                          {isEmailChecked && !isEmailChecking && (
                                            <span
                                              className={classNames('fs-8', {
                                                'text-danger': isEmailTaken,
                                                'text-success': !isEmailTaken,
                                              })}
                                            >
                                              {isEmailTaken
                                                ? intl.formatMessage({
                                                    id: 'INPUT.VALIDATION.EMAIL.TAKEN',
                                                  })
                                                : intl.formatMessage({
                                                    id: 'INPUT.VALIDATION.EMAIL.NOT_TAKEN',
                                                  })}
                                            </span>
                                          )}
                                        </>
                                      )}
                                    </div>

                                    {formik && get(formik.values, `email`)?.length > 0 && (
                                      <div className='d-flex flex-row-auto flex-end align-items-start'>
                                        <span className='fs-8 text-muted'>{`${
                                          get(formik.values, `email`)?.length || 0
                                        }/255`}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.TELEPHONE'})}
                            placeholder={intl.formatMessage({id: 'UTIL.TELEPHONE'})}
                            disabled={formik.isSubmitting}
                            formik={formik}
                            fieldName={`telephone`}
                            enableInputLength={true}
                          ></CustomInputRenderer>

                          <div className='row'>
                            <FieldArray name='details'>
                              {() => (
                                <>
                                  {get(formik.values, `details`) &&
                                    get(formik.values, `details`).map((detail, index) => (
                                      <div className='row mx-0 px-0' key={index}>
                                        <div className='col-xxl-3 my-2'>
                                          <div className='d-flex justify-content-end align-items-center'>
                                            <span className='text-white'>
                                              <span
                                                className='border badge bg-light-secondary cursor-pointer'
                                                style={{zIndex: '10'}}
                                                title={intl.formatMessage({id: 'UTIL.REMOVE'})}
                                                onClick={() =>
                                                  !formik.isSubmitting && removeDetail(index)
                                                }
                                              >
                                                <div className='d-flex flex-row justify-content-start align-items-center'>
                                                  <i
                                                    className={classNames({
                                                      'bi bi-x fs-2 text-danger':
                                                        !formik.isSubmitting,
                                                      'bi bi-x fs-2 text-secondary':
                                                        formik.isSubmitting,
                                                    })}
                                                  />
                                                </div>
                                              </span>
                                            </span>
                                          </div>
                                        </div>

                                        <div className='col-xxl-9'>
                                          <div className='row'>
                                            <div className='col-xxl-4 my-1'>
                                              <select
                                                className='form-select form-select-solid form-select-sm px-5 fs-6 text-dark'
                                                {...formik.getFieldProps(`details.${index}.type`)}
                                                onChange={(e) => formik.handleChange(e)}
                                                disabled={formik.isSubmitting}
                                              >
                                                <option value=''>
                                                  {intl.formatMessage({
                                                    id: 'UTIL.DROPDOWN.DEFAULT',
                                                  })}
                                                </option>
                                                <option value={ContactDetailsType.EMAIL}>
                                                  {intl.formatMessage({id: 'UTIL.EMAIL'})}
                                                </option>
                                                <option value={ContactDetailsType.FAX}>
                                                  {intl.formatMessage({id: 'UTIL.FAX'})}
                                                </option>
                                                <option value={ContactDetailsType.MOBILE}>
                                                  {intl.formatMessage({id: 'UTIL.MOBILE'})}
                                                </option>
                                                <option value={ContactDetailsType.OTHER}>
                                                  {intl.formatMessage({id: 'UTIL.OTHER'})}
                                                </option>
                                                <option value={ContactDetailsType.TELEPHONE}>
                                                  {intl.formatMessage({id: 'UTIL.TELEPHONE'})}
                                                </option>
                                                <option value={ContactDetailsType.SOCIAL}>
                                                  {intl.formatMessage({id: 'UTIL.SOCIAL'})}
                                                </option>
                                                <option value={ContactDetailsType.WEBSITE}>
                                                  {intl.formatMessage({id: 'UTIL.WEBSITE'})}
                                                </option>
                                              </select>
                                              <div className='d-flex flex-row p-1'>
                                                <div className='d-flex flex-column flex-row-fluid'>
                                                  <div className='d-flex flex-row flex-column-fluid'>
                                                    <div className='d-flex flex-row-fluid flex-start'>
                                                      {get(
                                                        formik.touched,
                                                        `details.${index}.type`
                                                      ) &&
                                                        get(
                                                          formik.errors,
                                                          `details.${index}.type`
                                                        ) && (
                                                          <span className='fs-8 text-danger'>
                                                            {get(
                                                              formik.errors,
                                                              `details.${index}.type`
                                                            )?.toUpperCase() ?? ''}
                                                          </span>
                                                        )}
                                                    </div>
                                                  </div>
                                                </div>
                                              </div>
                                            </div>

                                            <div className='col-xxl-8 my-1'>
                                              <input
                                                type='text'
                                                className='form-control form-control-sm form-control-solid px-5 py-0 fs-6 text-dark'
                                                placeholder={intl.formatMessage({
                                                  id: 'UTIL.INFORMATION',
                                                })}
                                                {...formik.getFieldProps(`details.${index}.value`)}
                                                disabled={formik.isSubmitting}
                                              />
                                              <div className='d-flex flex-row p-1'>
                                                <div className='d-flex flex-column flex-row-fluid'>
                                                  <div className='d-flex flex-row flex-column-fluid'>
                                                    <div className='d-flex flex-row-fluid flex-start'>
                                                      {get(
                                                        formik.touched,
                                                        `details.${index}.value`
                                                      ) &&
                                                        get(
                                                          formik.errors,
                                                          `details.${index}.value`
                                                        ) && (
                                                          <span className='fs-8 text-danger'>
                                                            {get(
                                                              formik.errors,
                                                              `details.${index}.value`
                                                            )?.toUpperCase() ?? ''}
                                                          </span>
                                                        )}
                                                    </div>

                                                    {formik &&
                                                      get(formik.values, `details.${index}.value`)
                                                        ?.length > 0 && (
                                                        <div className='d-flex flex-row-auto flex-end align-items-start'>
                                                          <span className='fs-8 text-muted'>{`${
                                                            get(
                                                              formik.values,
                                                              `details.${index}.value`
                                                            )?.length || 0
                                                          }/255`}</span>
                                                        </div>
                                                      )}
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  <div className='row mx-0 px-0 mt-2'>
                                    <div className='col-xxl-3'>
                                      <div className='d-flex justify-content-end align-items-center mb-3'>
                                        <span
                                          className='border badge bg-light-secondary cursor-pointer'
                                          style={{zIndex: '10'}}
                                          title={intl.formatMessage({id: 'UTIL.ADD'})}
                                          onClick={() => !formik.isSubmitting && addDetail()}
                                        >
                                          <div className='d-flex flex-row justify-content-start align-items-center'>
                                            <i
                                              className={classNames({
                                                'bi bi-plus fs-2 text-success':
                                                  !formik.isSubmitting,
                                                'bi bi-plus fs-2 text-secondary':
                                                  formik.isSubmitting,
                                              })}
                                            />
                                          </div>
                                        </span>
                                      </div>
                                    </div>
                                    <div className='col-xxl-9'></div>
                                  </div>
                                </>
                              )}
                            </FieldArray>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className='row mb-5'>
                      <SubSectionHeader title={intl.formatMessage({id: 'UTIL.LOCATION'})} />

                      <div className='px-5'>
                        <div className='row mb-5'>
                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.COUNTRY'})}
                            disabled={formik.isSubmitting}
                            formik={formik}
                            fieldName='countryCode'
                            object={
                              <select
                                className='form-select form-select-solid form-select-sm px-5 fs-6 text-dark'
                                {...formik.getFieldProps('countryCode')}
                                onChange={(e) => formik.handleChange(e)}
                                disabled={formik.isSubmitting}
                              >
                                <option value=''>
                                  {intl.formatMessage({id: 'UTIL.DROPDOWN.DEFAULT'})}
                                </option>
                                {COUNTRIES_EN &&
                                  COUNTRIES_EN.map((country, index) => (
                                    <option key={index} value={country.code}>
                                      {intl.formatMessage({id: `DROPDOWN.COUNTRY.${country.code}`})}
                                    </option>
                                  ))}
                              </select>
                            }
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.ADDRESS'})}
                            placeholder={intl.formatMessage({id: 'UTIL.ADDRESS'})}
                            disabled={formik.isSubmitting}
                            formik={formik}
                            fieldName={`address`}
                            enableInputLength={true}
                          ></CustomInputRenderer>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </FormikProvider>
        </div>
      </div>
    </>
  )
}
